/**
 * Apagar **todos** os pinouts — de um projeto, ou os internos de um componente.
 *
 * O gesto mais destrutivo que o sistema tem, e o único que existe: a API não
 * apaga "os filtrados" nem "os da página" — apaga tudo o que pertence ao alvo, de
 * uma vez, e devolve a contagem.
 *
 * **Mora aqui, onde o alvo está na frente, e não numa tela de manutenção.** Uma
 * tela que pede o id é exatamente onde se digita o id errado.
 *
 * **Confere antes.** O número da grade conta o que o filtro mostra; o apagar não
 * olha filtro nenhum. Então quem diz o tamanho é a conferência, que também conta
 * o cache do gráfico que cai junto e o **catálogo que fica sem dono** — componente,
 * conector, circuito, cor e chicote que nenhum outro pinout usa. O sistema antigo
 * apaga esse catálogo em cascata; esta API não apaga, e mostra a conta.
 *
 * **A confirmação é digitada.** Caixinha se marca sem ler. Digitar o nome obriga a
 * olhar para qual projeto é — que é o erro que essa operação comete: apagar o
 * pinout certo do jeito errado é raro; apagar o do alvo errado é o que acontece.
 *
 * **E ainda há dois portões no servidor**, que nenhuma tela substitui: a chave
 * `projeto_excluir` e o nível administrativo. Quem não tem recebe 403 com o motivo
 * — a conferência já leva o primeiro 403, antes de a pessoa digitar qualquer coisa.
 *
 * **O painel aberto flutua, ancorado no botão.** Ele mora numa barra de ferramentas
 * de 36px e `nowrap`: em fluxo normal, o relatório de 380px escapava para cima e
 * cobria a grade. Absoluto não briga com a barra e não obriga as duas telas que o
 * usam a abrir espaço para ele.
 */

import { useState } from "react"
import {
  usarApagarTodosOsPinouts, usarApagarTodosOsPinoutsInternos, usarConferirExclusao,
  type ConferenciaDeExclusao,
} from "@/api/manutencao"
import { ErroDaApi } from "@/api/erros"
import { ConfirmacaoDigitada } from "@/kit/ConfirmacaoDigitada"

/**
 * A recusa aqui merece nome próprio: a tela sabe qual é a chave, e "peça
 * `projeto_excluir` a quem administra os acessos" é acionável — "sem permissão"
 * não é.
 */
function porQueRecusou(erro: unknown): string {
  if (erro instanceof ErroDaApi && erro.ehSemPermissao) {
    return erro.problema?.detail
      ?? "Apagar em bloco exige a chave projeto_excluir no seu cadastro de operador. "
       + "Peça a quem administra os acessos."
  }
  return erro instanceof Error ? erro.message : "Não foi possível conferir."
}

export function ApagarTodosOsPinouts({
  projetoId, escopo = "projeto", alvoId, nomeDoAlvo, ancora = "direita", aoAvisar,
}: {
  /** Só para a trava do canário — o alvo pode ser o componente. */
  projetoId: number
  escopo?: "projeto" | "componente"
  /** O projeto, ou o componente quando o escopo é interno. */
  alvoId: number
  /** O que a pessoa vai ter de digitar. */
  nomeDoAlvo: string
  /**
   * De que lado do botão o cartão abre.
   *
   * O padrão é `direita`, que serve nas barras de ferramentas — o botão fica no fim
   * da barra e o cartão cresce para dentro da tela. Num botão à esquerda, `direita`
   * faria o cartão de 560px sair pela borda: aí é `esquerda`. É explícito de
   * propósito; medir a posição em tempo de abertura seria uma coisa a mais para dar
   * errado numa tela que apaga.
   */
  ancora?: "esquerda" | "direita"
  aoAvisar: (m: string | null) => void
}) {
  const [aberto, setAberto] = useState(false)

  const interno = escopo === "componente"
  const conferencia = usarConferirExclusao(escopo, alvoId, aberto)

  const externo = usarApagarTodosOsPinouts(projetoId)
  const internos = usarApagarTodosOsPinoutsInternos(projetoId, alvoId)
  const apagar = interno ? internos : externo

  const oQue = interno ? "pinouts internos" : "pinouts"
  const c = conferencia.data

  function fechar() { setAberto(false); aoAvisar(null) }

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => { if (aberto) fechar(); else { setAberto(true); aoAvisar(null) } }}
        aria-expanded={aberto}
        title={`Apaga todos os ${oQue} ${interno ? "deste componente" : "deste projeto"}. Não tem volta.`}
        style={{
          height: 26, padding: "0 10px", borderRadius: 7, fontSize: 11.5,
          border: "1px solid var(--line)",
          background: aberto ? "var(--surface2)" : "var(--surface)",
          color: "var(--txt3)", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        Apagar todos…
      </button>

      {aberto && <div style={{
        position: "absolute", top: "calc(100% + 6px)", zIndex: 30,
        ...(ancora === "direita" ? { right: 0 } : { left: 0 }),
        width: "min(560px, 88vw)", padding: 11, borderRadius: 9,
        border: "1px solid var(--line)", background: "var(--surface)",
        boxShadow: "0 10px 30px rgba(0,0,0,.28)",
      }}>
      {conferencia.isFetching && !c && (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)" }}>Conferindo o que seria apagado…</p>
      )}

      {conferencia.error && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 11.5, color: "var(--erro)", lineHeight: 1.5 }}>
            {porQueRecusou(conferencia.error)}
          </p>
          <button onClick={fechar}
                  style={{ border: 0, background: "transparent", cursor: "pointer",
                           color: "var(--txt3)", fontSize: 11.5 }}>
            fechar
          </button>
        </div>
      )}

      {c && <Relatorio c={c} oQue={oQue} />}

      {c && c.pinouts === 0 && (
        <button onClick={fechar}
                style={{ marginTop: 8, border: "1px solid var(--line)", borderRadius: 6,
                         padding: "4px 10px", fontSize: 11.5, cursor: "pointer",
                         background: "var(--surface)", color: "var(--txt2)" }}>
          Fechar
        </button>
      )}

      {c && c.pinouts > 0 && (
        <div style={{ marginTop: 8 }}>
          <ConfirmacaoDigitada
            alvo={nomeDoAlvo}
            rotuloDoBotao={`Apagar os ${c.pinouts.toLocaleString("pt-BR")} ${oQue}`}
            ocupado={apagar.isPending}
            oQueAcontece={
              <>
                Apaga <b>todos os {c.pinouts.toLocaleString("pt-BR")} {oQue}</b>{" "}
                {interno ? "do componente" : "do projeto"} <b>{nomeDoAlvo}</b>. Não é "os
                filtrados" nem "os desta página" — é tudo o que pertence a ele.{" "}
                <b>Não tem volta</b>, e o conteúdo publicado que dependia deles passa a não
                achar nada.
              </>
            }
            aoConfirmar={() =>
              apagar.mutate(undefined, {
                onSuccess: (r) => {
                  aoAvisar(`${r.excluidos.toLocaleString("pt-BR")} ${oQue} apagados.`)
                  setAberto(false)
                },
                onError: (e) => aoAvisar(porQueRecusou(e)),
              })
            }
            aoCancelar={() => setAberto(false)}
          />
        </div>
      )}
      </div>}
    </div>
  )
}

function Relatorio({ c, oQue }: { c: ConferenciaDeExclusao; oQue: string }) {
  return (
    <div style={{
      padding: 10, borderRadius: 6, maxHeight: "52vh", overflowY: "auto",
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
        {/* Quando o dono foi apagado o proprio nome ja e "componente 17152 (apagado)":
            repetir o id logo depois vira "17152 (#17152)". */}
        <b>{c.nome}</b>{c.alvoExiste && <> (#{c.alvoId})</>} ·{" "}
        <b>{c.pinouts.toLocaleString("pt-BR")}</b> {oQue}
        {c.restricoes > 0 && <> · {c.restricoes.toLocaleString("pt-BR")} restrição(ões)</>}
      </p>

      {c.avisos.map((a, i) => (
        <p key={i} style={{
          margin: "0 0 7px", padding: a.includes("NÃO TEM VOLTA") ? "6px 9px" : 0,
          borderRadius: 5, fontSize: 11, lineHeight: 1.5,
          background: a.includes("NÃO TEM VOLTA")
            ? "color-mix(in srgb, var(--erro) 12%, transparent)" : "transparent",
          color: a.includes("NÃO TEM VOLTA") ? "var(--erro)" : "var(--txt2)",
          border: a.includes("NÃO TEM VOLTA") ? "1px solid var(--erro)" : "0",
        }}>
          {a}
        </p>
      ))}

      {c.orfaos.length > 0 && (
        <div style={{ marginTop: 7, paddingTop: 7, borderTop: "1px dashed var(--line2)" }}>
          <p style={{ margin: "0 0 3px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
            Ficam no cadastro, sem pinout nenhum apontando para elas:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>
            {c.orfaos.map((o) => (
              <span key={o.tabela} style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                <span className="mono">{o.tabela}</span>{" "}
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{o.linhas.toLocaleString("pt-BR")}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
